/**
 * HR-only page to add, edit, or remove KPI assignments for any employee in any period.
 * Used for mid-year changes (new hire, role transfer, target revision).
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import adminApi from '@/modules/admin/api/adminApi'

export default function KPIRevisionPage() {
  const qc = useQueryClient()

  const [selectedPeriodId, setSelectedPeriodId] = useState('')
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')

  const [draftBobot, setDraftBobot] = useState<Record<string, string>>({})
  const [draftTarget, setDraftTarget] = useState<Record<string, string>>({})
  const [draftName, setDraftName] = useState<Record<string, string>>({})
  const [draftDesc, setDraftDesc] = useState<Record<string, string>>({})
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const [showAdd, setShowAdd] = useState(false)
  const [addMode, setAddMode] = useState<'existing' | 'new'>('existing')
  const [newKpiId, setNewKpiId] = useState('')
  const [newBobot, setNewBobot] = useState('')
  const [newTarget, setNewTarget] = useState('')
  const [newKpiName, setNewKpiName] = useState('')
  const [newKpiDesc, setNewKpiDesc] = useState('')

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: periodsData } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  const { data: usersData } = useQuery({
    queryKey: ['admin-users-all'],
    queryFn: () => adminApi.getUsers({ limit: 500 }),
    staleTime: 5 * 60 * 1000,
  })

  const { data: assignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['employee-kpis', selectedPeriodId, selectedEmployeeId],
    queryFn: () => penilaianApi.getEmployeeKPIAssignments(selectedPeriodId, selectedEmployeeId),
    enabled: !!selectedPeriodId && !!selectedEmployeeId,
  })

  const { data: periodKpis } = useQuery({
    queryKey: ['period-kpis', selectedPeriodId],
    queryFn: () => penilaianApi.getPeriodKPIs(selectedPeriodId),
    enabled: !!selectedPeriodId,
  })

  // ── Derived ──────────────────────────────────────────────────────────────

  const periods = periodsData?.data ?? []
  const users = usersData?.data ?? []


  const totalBobot = (assignments ?? []).reduce((sum, a) => sum + a.bobot, 0)
  const assignedKpiIds = new Set((assignments ?? []).map((a) => a.kpi_id))
  const availableKpis = (periodKpis ?? []).filter((k) => !assignedKpiIds.has(k.id) && k.is_active)

  const newBobotNum = parseFloat(newBobot)
  const projectedTotal = !isNaN(newBobotNum) ? totalBobot + newBobotNum : null

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!assignments) return
    const b: Record<string, string> = {}
    const t: Record<string, string> = {}
    const n: Record<string, string> = {}
    const d: Record<string, string> = {}
    for (const a of assignments) {
      b[a.id] = String(a.bobot)
      t[a.id] = a.target_value ?? ''
      n[a.id] = a.kpi?.name ?? ''
      d[a.id] = a.kpi?.target_description ?? ''
    }
    setDraftBobot(b)
    setDraftTarget(t)
    setDraftName(n)
    setDraftDesc(d)
  }, [assignments])

  // ── Mutations ─────────────────────────────────────────────────────────────

  const updateMutation = useMutation({
    mutationFn: ({ id, bobot, target }: { id: string; bobot: number; target: string | null }) =>
      penilaianApi.updateKPIAssignment(id, { bobot, target_value: target }),
    onSuccess: () => {
      toast.success('KPI diperbarui')
      qc.invalidateQueries({ queryKey: ['employee-kpis', selectedPeriodId, selectedEmployeeId] })
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal memperbarui'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => penilaianApi.deleteKPIAssignment(id),
    onSuccess: () => {
      toast.success('KPI dihapus')
      setDeleteConfirm(null)
      qc.invalidateQueries({ queryKey: ['employee-kpis', selectedPeriodId, selectedEmployeeId] })
    },
    onError: (e: any) => toast.error(e?.error?.message || 'Gagal menghapus'),
  })

  const addMutation = useMutation({
    mutationFn: async () => {
      const bobot = parseFloat(newBobot)
      if (isNaN(bobot) || bobot <= 0 || bobot > 100) throw new Error('Bobot harus antara 1–100')

      if (addMode === 'existing') {
        if (!newKpiId) throw new Error('Pilih KPI terlebih dahulu')
        return penilaianApi.assignKPI({
          employee_id: selectedEmployeeId,
          kpi_id: newKpiId,
          period_id: selectedPeriodId,
          bobot,
          target_value: newTarget.trim() || null,
        })
      } else {
        if (!newKpiName.trim()) throw new Error('Nama KPI harus diisi')
        const kpi = await penilaianApi.createKPI(selectedPeriodId, {
          period_id: selectedPeriodId,
          name: newKpiName.trim(),
          target_description: newKpiDesc.trim() || newKpiName.trim(),
          kpi_type: 'individual',
          order_no: (periodKpis?.length ?? 0) + 1,
        })
        return penilaianApi.assignKPI({
          employee_id: selectedEmployeeId,
          kpi_id: kpi.id,
          period_id: selectedPeriodId,
          bobot,
          target_value: newTarget.trim() || null,
        })
      }
    },
    onSuccess: () => {
      toast.success('KPI ditambahkan')
      setShowAdd(false)
      setNewKpiId(''); setNewBobot(''); setNewTarget(''); setNewKpiName(''); setNewKpiDesc('')
      qc.invalidateQueries({ queryKey: ['employee-kpis', selectedPeriodId, selectedEmployeeId] })
      qc.invalidateQueries({ queryKey: ['period-kpis', selectedPeriodId] })
    },
    onError: (e: any) => toast.error(e?.message || e?.error?.message || 'Gagal menambahkan'),
  })

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSaveRow = async (id: string, kpiId: string, origName: string, origDesc: string) => {
    const bobot = parseFloat(draftBobot[id] ?? '')
    if (isNaN(bobot) || bobot < 0 || bobot > 100) {
      toast.error('Bobot harus antara 0–100')
      return
    }
    const newName = draftName[id]?.trim() || ''
    const newDesc = draftDesc[id]?.trim() || ''
    try {
      if (newName !== origName || newDesc !== origDesc) {
        if (!newName) { toast.error('Nama KPI tidak boleh kosong'); return }
        await penilaianApi.updateKPIDefinition(kpiId, { name: newName, target_description: newDesc || newName })
        qc.invalidateQueries({ queryKey: ['period-kpis', selectedPeriodId] })
      }
      updateMutation.mutate({ id, bobot, target: draftTarget[id]?.trim() || null })
    } catch (e: any) {
      toast.error(e?.error?.message || 'Gagal memperbarui nama KPI')
    }
  }

  const handleSelectEmployee = (id: string) => {
    setSelectedEmployeeId(id)
    setDeleteConfirm(null)
    setShowAdd(false)
  }

  const bobatOk = totalBobot === 100
  const bobotBarColor = bobatOk ? 'bg-green-500' : totalBobot > 100 ? 'bg-red-500' : 'bg-amber-400'

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">

      {/* ── Header ── */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Revisi KPI Pegawai</h1>
        <p className="text-sm text-gray-500 mt-1">
          Tambah, ubah, atau hapus KPI untuk pegawai dalam periode tertentu
        </p>
      </div>

      {/* ── Selectors ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 grid grid-cols-2 gap-4">
        {/* Period */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Periode</label>
          <select
            value={selectedPeriodId}
            onChange={(e) => { setSelectedPeriodId(e.target.value); setSelectedEmployeeId(''); setShowAdd(false) }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— Pilih periode —</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Employee selector */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Pegawai</label>
          <select
            value={selectedEmployeeId}
            onChange={(e) => handleSelectEmployee(e.target.value)}
            disabled={!selectedPeriodId}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— Pilih pegawai —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.email}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Content ── */}
      {!selectedPeriodId && (
        <div className="text-center py-20 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-sm">Pilih periode dan pegawai untuk memulai</p>
        </div>
      )}

      {selectedPeriodId && !selectedEmployeeId && (
        <div className="text-center py-20 text-gray-400">
          <p className="text-sm">Cari dan pilih pegawai</p>
        </div>
      )}

      {selectedPeriodId && selectedEmployeeId && (
        <div className="space-y-4">

          {/* ── Bobot bar ── */}
          <div className={`rounded-xl border p-4 ${bobatOk ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Total Bobot</span>
                <span className={`text-2xl font-extrabold tabular-nums ${bobatOk ? 'text-green-600' : totalBobot > 100 ? 'text-red-600' : 'text-amber-600'}`}>
                  {totalBobot}%
                </span>
                {!bobatOk && (
                  <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                    {totalBobot < 100 ? `Kurang ${100 - totalBobot}%` : `Lebih ${totalBobot - 100}%`}
                  </span>
                )}
                {bobatOk && (
                  <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                    Seimbang
                  </span>
                )}
              </div>
              <button
                onClick={() => { setShowAdd((v) => !v); setDeleteConfirm(null) }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Tambah KPI
              </button>
            </div>
            {/* Progress bar */}
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${bobotBarColor}`}
                style={{ width: `${Math.min(100, totalBobot)}%` }}
              />
            </div>
          </div>

          {/* ── Add KPI panel ── */}
          {showAdd && (
            <div className="bg-white border border-blue-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Tambah KPI</h3>
                <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
              </div>

              {/* Mode toggle */}
              <div className="flex gap-2 mb-4">
                {(['existing', 'new'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setAddMode(mode)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      addMode === mode ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {mode === 'existing' ? 'Dari KPI yang ada' : 'Buat KPI baru'}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {addMode === 'existing' ? (
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">KPI</label>
                    <select
                      value={newKpiId}
                      onChange={(e) => setNewKpiId(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">— Pilih KPI —</option>
                      {availableKpis.map((k) => (
                        <option key={k.id} value={k.id}>
                          [{k.kpi_type === 'company' ? 'Korporat' : 'Individu'}] {k.name}
                        </option>
                      ))}
                    </select>
                    {availableKpis.length === 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        Semua KPI periode ini sudah ditugaskan ke pegawai ini. Gunakan "Buat KPI baru".
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Nama KPI</label>
                      <input
                        type="text"
                        value={newKpiName}
                        onChange={(e) => setNewKpiName(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="mis. Pencapaian Target Penjualan Regional"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Deskripsi Target</label>
                      <input
                        type="text"
                        value={newKpiDesc}
                        onChange={(e) => setNewKpiDesc(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="Deskripsi singkat target..."
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Bobot (%)
                  </label>
                  <input
                    type="number"
                    min={1} max={100}
                    value={newBobot}
                    onChange={(e) => setNewBobot(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="mis. 20"
                  />
                  {projectedTotal !== null && (
                    <p className={`text-xs mt-1 ${projectedTotal === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                      Total setelah tambah: <span className="font-semibold">{projectedTotal}%</span>
                      {projectedTotal === 100 && ' ✓'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Target Value <span className="text-gray-400 font-normal">(opsional)</span>
                  </label>
                  <input
                    type="text"
                    value={newTarget}
                    onChange={(e) => setNewTarget(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="mis. 95%"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowAdd(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  onClick={() => addMutation.mutate()}
                  disabled={addMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors"
                >
                  {addMutation.isPending ? 'Menambahkan...' : 'Tambah KPI'}
                </button>
              </div>
            </div>
          )}

          {/* ── KPI table ── */}
          {assignmentsLoading ? (
            <div className="text-center py-12 text-gray-400">Memuat...</div>
          ) : (assignments ?? []).length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-gray-300 rounded-xl">
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-gray-500 font-medium">Belum ada KPI</p>
              <p className="text-sm text-gray-400 mt-1">Pegawai ini belum memiliki KPI di periode ini</p>
              <button
                onClick={() => setShowAdd(true)}
                className="mt-3 text-sm text-blue-600 hover:underline font-medium"
              >
                + Tambah KPI pertama
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">KPI</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Tipe</th>
                    <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Bobot (%)</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Target</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Realisasi</th>
                    <th className="w-32 px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(assignments ?? []).map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3 min-w-[200px]">
                        <input
                          type="text"
                          value={draftName[a.id] ?? ''}
                          onChange={(e) => setDraftName((p) => ({ ...p, [a.id]: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Nama KPI"
                        />
                        <input
                          type="text"
                          value={draftDesc[a.id] ?? ''}
                          onChange={(e) => setDraftDesc((p) => ({ ...p, [a.id]: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-500 mt-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Deskripsi target (opsional)"
                        />
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          a.kpi?.kpi_type === 'company'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {a.kpi?.kpi_type === 'company' ? 'Korporat' : 'Individu'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          min={0} max={100}
                          value={draftBobot[a.id] ?? ''}
                          onChange={(e) => setDraftBobot((p) => ({ ...p, [a.id]: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          value={draftTarget[a.id] ?? ''}
                          onChange={(e) => setDraftTarget((p) => ({ ...p, [a.id]: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="—"
                        />
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">
                        {a.realisasi != null ? (
                          <span>
                            {a.realisasi}
                            {a.realisasi_pct != null && (
                              <span className={`ml-1.5 text-xs font-semibold ${
                                a.realisasi_pct >= 100 ? 'text-green-600' :
                                a.realisasi_pct >= 75 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                ({a.realisasi_pct}%)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-300 italic">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {deleteConfirm === a.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => deleteMutation.mutate(a.id)}
                              disabled={deleteMutation.isPending}
                              className="px-2 py-1 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-40"
                            >
                              Hapus
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="px-2 py-1 border border-gray-300 text-gray-600 rounded-lg text-xs hover:bg-gray-50"
                            >
                              Batal
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleSaveRow(a.id, a.kpi_id, a.kpi?.name ?? '', a.kpi?.target_description ?? '')}
                              disabled={updateMutation.isPending}
                              className="px-2.5 py-1 bg-gray-800 text-white rounded-lg text-xs font-medium hover:bg-gray-900 disabled:opacity-40"
                            >
                              Simpan
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(a.id)}
                              className="p-1 text-gray-300 hover:text-red-500 rounded-lg transition-colors"
                              title="Hapus KPI"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                  <tr>
                    <td colSpan={2} className="px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Total
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`text-sm font-extrabold tabular-nums ${bobatOk ? 'text-green-600' : 'text-red-600'}`}>
                        {totalBobot}%
                      </span>
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
